/**
 * Steps in the scheduling typeahead guided flow.
 *
 * ```
 * idle → matching → trigger_selected → [context] → [connectorService] → [venue] → [serviceProvider] → [partySize] → [contacts] → time → duration → [location] → title → sent
 * ```
 *
 * Visibility depends on flow type:
 * - meeting: contacts (multi), location
 * - appointment: serviceProvider (single-select "Where?")
 * - reservation: venue (single-select "Where?"), partySize
 * - connector: connectorService only (single-select "Which service?")
 */
export type FlowStep =
  | "idle"
  | "matching"
  | "trigger_selected"
  | "context"
  | "connectorService"
  | "venue"
  | "serviceProvider"
  | "partySize"
  | "contacts"
  | "time"
  | "duration"
  | "location"
  | "title"
  | "sent";

/** The flow type determines which steps are shown. */
export type FlowType = "meeting" | "appointment" | "reservation" | "connector";

/** Ordered sequence of guided-flow steps (after trigger selection). */
export const FLOW_STEPS: FlowStep[] = [
  "trigger_selected",
  "context",
  "connectorService",
  "venue",
  "serviceProvider",
  "partySize",
  "contacts",
  "time",
  "duration",
  "location",
  "title",
  "sent",
];

/** Steps that display the guided-flow UI (progress dots, keyboard nav). */
export const GUIDED_STEPS: FlowStep[] = FLOW_STEPS.filter(
  (s) => s !== "trigger_selected" && s !== "sent",
);

/** Human-readable labels for each guided step. */
export const STEP_LABELS: Partial<Record<FlowStep, string>> = {
  context: "What kind?",
  connectorService: "Which service?",
  venue: "Where?",
  serviceProvider: "Where?",
  partySize: "How many?",
  contacts: "With who?",
  time: "When?",
  duration: "How long?",
  location: "Where?",
  title: "Title",
};

/**
 * A contact entry gathered during the contacts step.
 * Re-uses the existing `Contact` type for known contacts; free-text entries
 * set `isNew: true` and omit the `id`.
 */
export interface ContactEntry {
  /** Okthx contact ID — present for known contacts, absent for free-text entries. */
  id?: string;
  name: string;
  email?: string;
  phone?: string;
  /** `true` when the user typed a name/email not found in the contacts list. */
  isNew: boolean;
}

/** Location selection from the guided flow. */
export interface LocationEntry {
  type: "virtual" | "in-person";
  /** Address or location name for in-person meetings. */
  details?: string;
}

/** A segment of the composed scheduling text. Segments with `step` are clickable/bold in the overlay. */
export interface TextSegment {
  text: string;
  /** When present, this segment was filled via the given step and is clickable. */
  step?: FlowStep;
}

/** Result from the trigger-word matching algorithm. */
export interface MatchResult {
  /** The trigger word that matched. */
  word: string;
  /** Match confidence score (0–1). Prefix matches score 1.0. */
  score: number;
  /** Whether the match was an exact prefix or a fuzzy subsequence. */
  matchType: "prefix" | "fuzzy";
}

/** Accumulated state for the scheduling typeahead guided flow. */
export interface TypeaheadState {
  step: FlowStep;
  /** The flow type determines which steps are visible. */
  flowType: FlowType;
  triggerWord: string | null;
  /** Additional context for the trigger word (e.g. "coffee" for "Grab", "a haircut" for "Book"). */
  context: string | null;
  /** Single-select venue for reservations (e.g. "Olive Garden"). */
  venue: ContactEntry | null;
  /** Single-select service provider for appointments (e.g. "Supercuts"). */
  serviceProvider: ContactEntry | null;
  /** Party size for reservations (e.g. "4 people"). */
  partySize: string | null;
  contacts: ContactEntry[];
  /** Raw text or structured quick-pick value for time/date. */
  timeDate: string | null;
  /** User-selected availability windows from the calendar picker (for editing/back navigation). */
  userConstraintRanges: Array<{ startISO: string; endISO: string }> | null;
  /** Whether to skip calendar conflict checking for custom availability. */
  skipCalendarConflicts: boolean | null;
  duration: string | null;
  location: LocationEntry | null;
  title: string | null;
  /** Selected connector service name for the connector flow (e.g. "Notion"). */
  connectorService: string | null;
  /**
   * Snapshot (at SELECT_TRIGGER time) of which scheduling-flow fields have been
   * answered out-of-band by an active omnibox enrichment. Fields set to `true`
   * here cause `advanceStep` to skip the matching user-facing step (e.g.
   * `whenProvided` skips the "when?" step). Kept enrichment-agnostic: any
   * enrichment declaring `provides` is honored uniformly.
   */
  externalInputs: ExternalInputs;
}

/**
 * Which scheduling-flow fields are satisfied by out-of-band input (an active
 * omnibox enrichment). See `OmniboxEnrichment.provides`.
 */
export interface ExternalInputs {
  whenProvided: boolean;
  durationProvided: boolean;
}

/** Discriminated union of actions dispatched to the typeahead reducer. */
export type TypeaheadAction =
  | { type: "SET_STEP"; step: FlowStep }
  | { type: "SELECT_TRIGGER"; triggerWord: string; externalInputs: ExternalInputs }
  | { type: "SET_CONTEXT"; context: string; flowType: FlowType }
  | { type: "SET_CONNECTOR_SERVICE"; connectorService: string }
  | { type: "SET_VENUE"; venue: ContactEntry }
  | { type: "SET_SERVICE_PROVIDER"; serviceProvider: ContactEntry }
  | { type: "SET_PARTY_SIZE"; partySize: string }
  | { type: "ADD_CONTACT"; contact: ContactEntry }
  | { type: "REMOVE_CONTACT"; contactIndex: number }
  | {
      type: "SET_TIME";
      timeDate: string;
      userConstraintRanges?: Array<{ startISO: string; endISO: string }>;
      skipCalendarConflicts?: boolean;
    }
  | { type: "SET_DURATION"; duration: string }
  | { type: "SET_LOCATION"; location: LocationEntry }
  | { type: "SET_TITLE"; title: string }
  | { type: "RESET" }
  | { type: "SKIP_STEP" };
